// Pure layout seam for the work dashboard grid (W12 Track A, spec §6 A3).
//
// The W1 drag kernel moves DOM items between containers and reports
// neighbour deltas; THIS module is the only place that turns those deltas
// into dashboard-layout operations and applies them to the working layout
// (W12-D2: "drop → move id before/after neighbour id … never from absolute
// order"). Pure and dependency-free — unit-testable without a DOM
// (grid_layout.test.ts), the work_rank_helpers.test.ts precedent.
//
// Layout model (W12-D1): a 12-column row-flow grid, no free x/y, no holes.
// A layout is an ordered array of entries `{gadget_id, width, height}`;
// the persisted state is host-owned and arrives/departs verbatim —
// operations are immutable (applyOp never mutates its input).
//
// CONEK-4938 (W12-D5): layout entries whose gadget_id is absent from the
// registry are NEVER rendered and NEVER dropped or reordered — they ride
// verbatim at their position in every emitted payload, so a save can never
// silently delete a gated-off gadget. layoutForRender is the split point.

// Container ids the dashboard emits on its two kernel containers. The grid
// body is "grid", the edit-mode tray "tray" (W12-D2).
export const GRID_CONTAINER_ID = "grid"
export const TRAY_CONTAINER_ID = "tray"

// Column span ceiling (W12-D1 constant: COLUMNS = 12).
export const MAX_SPAN = 12

// Default spans for a registry entry (W12-D1: width default 6, height 4;
// min spans 2/2 per B1).
export const DEFAULT_SPANS = { width: 6, height: 4, min_width: 2, min_height: 2 }

function toId(value) {
  return value === null || value === undefined ? null : String(value)
}

function toSpan(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback
}

// Normalize a raw layout entry (host hash or payload fragment) to
// `{gadget_id, width, height}`. Entries without a gadget_id are null.
export function normalizeEntry(entry) {
  if (!entry || typeof entry !== "object") return null
  const gadget_id = toId(entry.gadget_id)
  if (!gadget_id) return null
  return {
    gadget_id,
    width: toSpan(entry.width, DEFAULT_SPANS.width),
    height: toSpan(entry.height, DEFAULT_SPANS.height),
  }
}

// True when the registry carries the gadget. The registry is a plain
// `{[gadget_id]: {width, height, min_width, min_height}}` map the engine
// builds from the SSR wrapper data attributes (B1: clamps read the DOM
// contract, never JS-side constants).
export function isRegistered(registry, gadget_id) {
  return Boolean(registry && Object.prototype.hasOwnProperty.call(registry, gadget_id))
}

// Clamp one layout entry against the registry (the M1 mutation target):
// width → [min_width, 12], height → [min_height, 12], defaults filled from
// the registry def. The height ceiling is the D1 MAX_SPAN — the SSR clamp
// (GadgetFrameComponent), the runtime resize clamp (_clampSpan) and the CSS
// safelist (row-span-1..12) all end at 12, so an unbounded host height would
// hydrate to a row-span class no compiled CSS carries. Unregistered ids pass
// through untouched (they are not rendered; clamping them would be silent
// data editing — CONEK-4938's verbatim rule covers the whole entry, not just
// its position).
export function clampEntry(entry, registry) {
  const normalized = normalizeEntry(entry)
  if (!normalized) return null
  if (!isRegistered(registry, normalized.gadget_id)) return normalized

  const def = registry[normalized.gadget_id] || {}
  const minWidth = toSpan(def.min_width, DEFAULT_SPANS.min_width)
  const minHeight = toSpan(def.min_height, DEFAULT_SPANS.min_height)

  return {
    ...normalized,
    width: Math.min(MAX_SPAN, Math.max(minWidth, normalized.width)),
    height: Math.min(MAX_SPAN, Math.max(minHeight, normalized.height)),
  }
}

// The insertion slot for a neighbour delta. `before_id` is the new
// predecessor, `after_id` the new successor (kernel rank contract,
// anti-idea 3); either may be null, both null = append at the end.
// Returns an index into `order`, or order.length when the slot is append.
function insertionIndex(order, beforeId, afterId) {
  if (beforeId !== null) {
    const index = order.indexOf(beforeId)
    if (index !== -1) return index + 1
  }
  if (afterId !== null) {
    const index = order.indexOf(afterId)
    if (index !== -1) return index
  }
  return order.length
}

// Translate a kernel `ui--work:drop` payload into a layout operation
// (W12-D2). The payload is the kernel's neighbour-delta detail:
// `{container, from_container, moved_ids[], before_id, after_id,
// gesture_token, source}`. Cross-container semantics:
//
//   tray → grid : add    (the gadget joins the working layout at the slot)
//   grid → grid : move   (reorder within the grid)
//   grid → tray : remove (the gadget leaves the layout, back to the tray)
//   tray → tray : null   (the tray is an unordered list — no layout intent)
//
// Returns `null` for anything that is not a layout intent (empty payload,
// unknown container ids, an empty moved_ids list).
export function translateDrop(drop, layout = []) {
  if (!drop || typeof drop !== "object") return null

  const container = toId(drop.container)
  const from = toId(drop.from_container)
  const gadget_id = toId((drop.moved_ids || [])[0])
  if (!gadget_id) return null

  if (container === GRID_CONTAINER_ID) {
    if (from === TRAY_CONTAINER_ID) {
      if (layout.some((entry) => entry.gadget_id === gadget_id)) return null
      return {
        op: "add",
        gadget_id,
        before_id: toId(drop.before_id),
        after_id: toId(drop.after_id),
        gesture_token: drop.gesture_token ?? null,
      }
    }
    return {
      op: "move",
      gadget_id,
      before_id: toId(drop.before_id),
      after_id: toId(drop.after_id),
      gesture_token: drop.gesture_token ?? null,
    }
  }

  if (container === TRAY_CONTAINER_ID && from === GRID_CONTAINER_ID) {
    return {
      op: "remove",
      gadget_id,
      before_id: toId(drop.before_id),
      after_id: toId(drop.after_id),
      gesture_token: drop.gesture_token ?? null,
    }
  }

  return null
}

// Apply one operation to `layout` IMMUTABLY, returning
// `{layout: newLayout, applied: boolean}`. Operations:
//
//   {op: "move", gadget_id, before_id, after_id}
//   {op: "add", gadget_id, before_id, after_id}   — spans from the registry
//   {op: "remove", gadget_id}                      — registered ids only
//   {op: "reset", registry}                        — defaults + CONEK-4938 merge
//   {op: "clean", registry}                        — registered entries out
//
// `applied: false` means the op was a no-op (unknown id, duplicate add, a
// remove that would delete a preserved unregistered entry) — the caller
// emits nothing and mutates nothing.
export function applyOp(layout, op, registry = {}, defaults = []) {
  const source = Array.isArray(layout) ? layout : []
  if (!op || typeof op !== "object") return { layout: source, applied: false }

  if (op.op === "remove") {
    const gadget_id = toId(op.gadget_id)
    if (!isRegistered(registry, gadget_id)) return { layout: source, applied: false }
    if (!source.some((entry) => entry.gadget_id === gadget_id)) {
      return { layout: source, applied: false }
    }
    return { layout: source.filter((entry) => entry.gadget_id !== gadget_id), applied: true }
  }

  if (op.op === "clean") {
    return {
      layout: source.filter((entry) => !isRegistered(registry, entry.gadget_id)),
      applied: true,
    }
  }

  if (op.op === "reset") {
    // Reset targets the host-injected defaults (W12-D4/RB7); preserved
    // unregistered entries from the CURRENT layout ride at their index so a
    // reset can never silently delete a gated-off gadget (CONEK-4938).
    const base = (Array.isArray(defaults) ? defaults : [])
      .map((entry) => clampEntry(entry, registry))
      .filter(Boolean)
      .filter((entry) => isRegistered(registry, entry.gadget_id))

    const result = [...base]
    source.forEach((entry, index) => {
      if (isRegistered(registry, entry.gadget_id)) return
      if (result.some((placed) => placed.gadget_id === entry.gadget_id)) return
      result.splice(Math.min(index, result.length), 0, { ...entry })
    })
    return { layout: result, applied: true }
  }

  if (op.op === "add") {
    const gadget_id = toId(op.gadget_id)
    if (!isRegistered(registry, gadget_id)) return { layout: source, applied: false }
    if (source.some((entry) => entry.gadget_id === gadget_id)) {
      return { layout: source, applied: false }
    }
    const def = registry[gadget_id] || {}
    const entry = clampEntry(
      {
        gadget_id,
        width: def.width,
        height: def.height,
      },
      registry,
    )
    const ids = source.map((placed) => placed.gadget_id)
    const index = insertionIndex(ids, toId(op.before_id), toId(op.after_id))
    const result = [...source]
    result.splice(index, 0, entry)
    return { layout: result, applied: true }
  }

  if (op.op === "move") {
    const gadget_id = toId(op.gadget_id)
    const index = source.findIndex((entry) => entry.gadget_id === gadget_id)
    if (index === -1) return { layout: source, applied: false }
    const entry = source[index]
    const rest = source.filter((_, i) => i !== index)
    const ids = rest.map((placed) => placed.gadget_id)
    const insertAt = insertionIndex(ids, toId(op.before_id), toId(op.after_id))
    const result = [...rest]
    result.splice(insertAt, 0, entry)
    return { layout: result, applied: true }
  }

  return { layout: source, applied: false }
}

// The CONEK-4938 render split: `rendered` carries the registered entries in
// layout order (they have DOM wrappers); `preserved` carries unregistered
// entries verbatim. The component renders `rendered` only; every emitted
// payload is the FULL layout (preserved entries included).
//
// HOST/TEST UTILITY: the engine's own split is the registry check inline
// (rendered_gadgets on the Ruby side); nothing in the runtime imports this —
// it exists for hosts composing their own render paths and for the seam's
// unit tests (grid_layout.test.ts).
export function layoutForRender(layout, registry = {}) {
  const source = Array.isArray(layout) ? layout : []
  return {
    rendered: source.filter((entry) => isRegistered(registry, entry.gadget_id)),
    preserved: source.filter((entry) => !isRegistered(registry, entry.gadget_id)),
  }
}

// The responsive stack (W12-D1 mobile rule) as data: every entry collapses
// to full width, order preserved. No JS ships for the render path itself —
// the collapse is pure CSS (`max-md:col-span-12` on every wrapper).
//
// HOST/TEST UTILITY: nothing in the runtime imports this — it exists for
// hosts doing tray/aria math over a collapsed layout and for the seam's
// unit tests (grid_layout.test.ts).
export function mobileLayout(layout) {
  const source = Array.isArray(layout) ? layout : []
  return source.map((entry) => ({ ...entry, width: MAX_SPAN }))
}
